import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Alert, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api';
import { useProducts } from '../hooks/useProducts';
import { useBulkTranslate } from '../hooks/useBulkTranslate';
import { useBulkPublishEmbed, BulkPublishEmbedResult } from '../hooks/useBulkPublishEmbed';
import {
  ProductCard,
  SearchFilters,
  ProductListSkeleton,
  EmptyState,
  ProductPagination,
  ProductListTable,
  ProductViewMode,
  BulkActionToolbar,
  BulkTranslateDialog,
  BulkResultsDialog,
} from '../components/products';
import PageHeader from '../components/PageHeader';
import { BulkTranslateResult } from '../components/products/types';

const PRODUCT_VIEW_MODE_STORAGE_KEY = 'products:viewMode';

const getInitialViewMode = (): ProductViewMode => {
  try {
    const storedViewMode = window.localStorage.getItem(PRODUCT_VIEW_MODE_STORAGE_KEY);
    return storedViewMode === 'cards' || storedViewMode === 'list' ? storedViewMode : 'list';
  } catch {
    return 'list';
  }
};

const Products: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const api = useApi();
  const [viewMode, setViewMode] = useState<ProductViewMode>(getInitialViewMode);
  const {
    products,
    loading,
    error,
    query,
    setQuery,
    sortBy,
    sortDirection,
    setSort,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    refetch,
  } = useProducts();

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [allIdsLoading, setAllIdsLoading] = useState(false);
  const allSelected = selectedIds.size > 0 && selectedIds.size >= totalCount;

  // Bulk translate dialog state
  const [translateDialogOpen, setTranslateDialogOpen] = useState(false);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [bulkTargetLanguage, setBulkTargetLanguage] = useState('');
  const [bulkResult, setBulkResult] = useState<BulkTranslateResult | null>(null);

  const { bulkTranslate, loading: bulkLoading } = useBulkTranslate();
  const { bulkPublish, loading: publishLoading } = useBulkPublishEmbed();
  const [publishResult, setPublishResult] = useState<BulkPublishEmbedResult | null>(null);

  const handleProductView = useCallback(
    (productId: string) => {
      navigate(`/products/${productId}`);
    },
    [navigate]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
      setPage(1);
    },
    [setPageSize, setPage]
  );

  const handleCreateProduct = useCallback(() => {
    navigate('/products/new');
  }, [navigate]);

  const handleViewModeChange = useCallback((nextViewMode: ProductViewMode) => {
    setViewMode(nextViewMode);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(PRODUCT_VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      // Local storage can be unavailable in locked-down browsers; the default view still works.
    }
  }, [viewMode]);

  // Selection handlers
  const handleEnterSelectionMode = useCallback(() => {
    setSelectionMode(true);
  }, []);

  const handleExitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleToggleSelect = useCallback((productId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(
    (selectAll: boolean) => {
      if (selectAll) {
        setSelectedIds(new Set(products.map((p) => p.id)));
      } else {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          products.forEach((p) => next.delete(p.id));
          return next;
        });
      }
    },
    [products]
  );

  const handleSelectAllPages = useCallback(async () => {
    setAllIdsLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.set('query', query);
      const data = await api.get(`/products/ids?${params.toString()}`);
      const ids: string[] = (data as { ids: string[] })?.ids ?? [];
      setSelectedIds(new Set(ids));
    } catch {
      // silently ignore - user can retry
    } finally {
      setAllIdsLoading(false);
    }
  }, [api, query]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Bulk translate handlers
  const handleOpenTranslateDialog = useCallback(() => {
    setTranslateDialogOpen(true);
  }, []);

  const handleCloseTranslateDialog = useCallback(() => {
    if (!bulkLoading) {
      setTranslateDialogOpen(false);
    }
  }, [bulkLoading]);

  const handleConfirmTranslate = useCallback(
    async (targetLanguage: string) => {
      setBulkTargetLanguage(targetLanguage);
      const result = await bulkTranslate(Array.from(selectedIds), targetLanguage);
      setTranslateDialogOpen(false);
      if (result) {
        setBulkResult(result);
        setResultsDialogOpen(true);
      }
    },
    [bulkTranslate, selectedIds]
  );

  const handleCloseResultsDialog = useCallback(() => {
    setResultsDialogOpen(false);
    setBulkResult(null);
    handleExitSelectionMode();
    refetch();
  }, [handleExitSelectionMode, refetch]);

  const handleBulkPublish = useCallback(async () => {
    const result = await bulkPublish(Array.from(selectedIds));
    if (result) {
      setPublishResult(result);
      handleExitSelectionMode();
    }
  }, [bulkPublish, selectedIds, handleExitSelectionMode]);

  const renderProducts = () => {
    if (loading) {
      return <ProductListSkeleton viewMode={viewMode} />;
    }

    if (products.length === 0) {
      return <EmptyState hasQuery={Boolean(query)} searchQuery={query} />;
    }

    if (viewMode === 'list') {
      return (
        <ProductListTable
          products={products}
          onView={handleProductView}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={setSort}
          selectionMode={selectionMode}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onToggleSelectAll={handleToggleSelectAll}
          onEnterSelectionMode={handleEnterSelectionMode}
        />
      );
    }

    return (
      <Grid container spacing={3}>
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onView={handleProductView}
          />
        ))}
      </Grid>
    );
  };

  return (
    <Box>
      <PageHeader
        title={t('products.title')}
        subtitle={t('products.subtitle')}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateProduct}>
            {t('products.newProduct')}
          </Button>
        }
      />

      <SearchFilters
        query={query}
        onQueryChange={setQuery}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />

      {selectionMode && (
        <BulkActionToolbar
          selectedCount={selectedIds.size}
          totalCount={totalCount}
          allSelected={allSelected}
          loadingAllIds={allIdsLoading}
          onSelectAll={handleSelectAllPages}
          onDeselectAll={handleDeselectAll}
          onTranslate={handleOpenTranslateDialog}
          onPublish={handleBulkPublish}
          publishLoading={publishLoading}
          onCancel={handleExitSelectionMode}
        />
      )}

      {publishResult && (
        <Alert
          severity={publishResult.failed_count > 0 ? 'warning' : 'success'}
          onClose={() => setPublishResult(null)}
          sx={{ mb: 2 }}
        >
          {t('products.bulk.publishResult',
            'Embedding enabled for {{published}} product(s). Already active: {{already}}. Failed: {{failed}}.',
            {
              published: publishResult.published_count,
              already: publishResult.already_published_count,
              failed: publishResult.failed_count,
            }
          )}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {renderProducts()}

      {!loading && !error && (
        <ProductPagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      <BulkTranslateDialog
        open={translateDialogOpen}
        selectedCount={selectedIds.size}
        onClose={handleCloseTranslateDialog}
        onConfirm={handleConfirmTranslate}
        loading={bulkLoading}
      />

      <BulkResultsDialog
        open={resultsDialogOpen}
        result={bulkResult}
        targetLanguage={bulkTargetLanguage}
        onClose={handleCloseResultsDialog}
      />
    </Box>
  );
};

export default Products;
